<script lang="ts">
	// T31 Close shift (UC-60), over Shift, in three steps of one question
	// each. First the till asks the tills that send their cash here for the
	// sales it lacks, a step a till no other till points to skips. Then a
	// manager types the count, blind: no expected figure, float or sale total
	// shows on that step. Last, what the close came to, over or short first,
	// and Due at close split by driver when the store split it (UC-60.R7).
	//
	// Only a manager closes. A manager signed in closes by their own session;
	// with anyone else, Confirm hands over to Manager approval (T20), and this
	// sheet steps aside while it is up and comes back with the count kept, as
	// Cancel item does (T19). The close keeps one key for every try, so a try
	// whose answer was lost is answered as before (INT-7).

	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Sheet from '../components/Sheet.svelte';
	import StatCard from '../components/StatCard.svelte';
	import { formatMinor, parseTypedMinor } from '../format/money';
	import { overShort } from '../format/shift';
	import { fill, isArabic, message, type Language } from '../i18n/messages';
	import { PasscodeEntry, retryAfterSeconds, type PasscodeResult } from '../till/passcode.svelte';
	import { Roster } from '../till/roster.svelte';
	import {
		askShift,
		closeShiftAttempt,
		loadShift,
		printShiftAgain,
		shiftBody,
		stillAsking,
		watchAsk,
		type ClosedShift,
	} from '../till/shift.svelte';
	import DueByDriver from './DueByDriver.svelte';
	import ManagerApproval from './ManagerApproval.svelte';
	import { typeTender } from './Payment.svelte';

	interface Props {
		open: boolean;
		language: Language;
		minorDigits: number;
		/** The person signed in is a manager, who closes by their own session. */
		isManager: boolean;
		/** This till's name, for the approval's title. */
		tillName: string;
		/** Who is signed in: the manager a close with no approval is closed as. */
		userId: string;
		/**
		 * Told whether a count is typed, kept while the sheet is closed, or a
		 * close or its print is on its way: the till's own lock installs no app
		 * update over it (#925).
		 */
		onHolding?: (holding: boolean) => void;
		onClose: () => void;
	}

	let { open, language, minorDigits, isManager, tillName, userId, onHolding, onClose }: Props =
		$props();

	type Step = 'start' | 'ask' | 'count' | 'result';
	let step = $state<Step>('start');
	let typed = $state('');
	let asking = $state(false);
	let askSlow = $state(false);
	let closing = $state(false);
	let slow = $state(false);
	let failure = $state('');
	let result = $state<ClosedShift | null>(null);
	let approving = $state(false);
	let printing = $state(false);
	let printSlow = $state(false);
	let printFailure = $state('');
	let send = closeShiftAttempt();
	let stopWatch = () => {};

	$effect(() => {
		onHolding?.(typed !== '' || closing || printing || approval.phase === 'checking');
	});

	const body = $derived(shiftBody());
	const countedMinor = $derived(parseTypedMinor(typed));
	const asked = $derived(body?.asked ?? []);
	const received = $derived(asked.every((till) => till.state === 'answered'));
	const approvers = new Roster();

	// Each opening starts where the last left off, so a count typed and
	// closed away is still there; once a shift closed, the next opening is
	// another shift's and starts again.
	$effect(() => {
		if (!open) {
			// A lock, or the sheet closed: Manager approval goes with it, so
			// the next opening starts at the count with no manager picked.
			untrack(() => {
				approving = false;
				approval.unpick();
			});
			return;
		}
		untrack(() => {
			if (step === 'result') {
				step = 'start';
				typed = '';
				result = null;
				send = closeShiftAttempt();
			}
			failure = '';
			if (step === 'start' || step === 'ask') void ask();
		});
		return () => stopWatch();
	});

	$effect(() => () => {
		stopWatch();
		approval.stop();
	});

	async function ask() {
		asking = true;
		failure = '';
		stopWatch();
		// A wait shows only after 300 ms: the ask answers at once, and a till
		// no other till points to goes straight on to the count.
		const timer = setTimeout(() => (askSlow = true), 300);
		try {
			const read = await askShift();
			if (read.asked.length === 0) {
				step = 'count';
				return;
			}
			step = 'ask';
			if (stillAsking(read)) stopWatch = watchAsk();
		} catch {
			// With no answer the till cannot say which tills it reached:
			// Close anyway still moves on, and they read as not reached.
			step = 'ask';
			failure = message(language, 'offline');
		} finally {
			clearTimeout(timer);
			asking = false;
			askSlow = false;
		}
	}

	/** What a close came to, for the count step and the approval sheet. */
	type Closed =
		| { kind: 'closed'; closed: ClosedShift }
		| { kind: 'approval'; result: PasscodeResult<ClosedShift> }
		| { kind: 'refused'; words: string };

	async function close(approval?: { userId: string; passcode: string }): Promise<Closed> {
		const shiftId = body?.open?.shiftId;
		if (countedMinor === null || !shiftId) {
			return { kind: 'refused', words: message(language, 'shiftAlreadyClosed') };
		}
		try {
			const { data, error, response } = await send(
				{
					shiftId,
					countedMinor,
					...(approval ? { approval } : {}),
				},
				userId,
			);
			if (error === undefined && data?.lastClosed)
				return { kind: 'closed', closed: data.lastClosed };
			const code = error?.code ?? '';
			if (approval && code === 'PASSCODE_WRONG') {
				return { kind: 'approval', result: { ok: false, failure: 'wrong' } };
			}
			if (approval && response.status === 423) {
				return {
					kind: 'approval',
					result: {
						ok: false,
						failure: 'locked',
						lockSeconds: retryAfterSeconds(error?.details),
						wholeRestaurant: error?.details?.scope === 'account',
					},
				};
			}
			if (approval && code === 'FORBIDDEN') {
				return { kind: 'approval', result: { ok: false, failure: 'forbidden' } };
			}
			if (approval && response.status === 503) {
				// No answer from Bosla to the passcode check: the shift stays open.
				return { kind: 'approval', result: { ok: false, failure: 'internet' } };
			}
			if (code === 'SHIFT_CLOSED' || code === 'IDEMPOTENCY_MISMATCH') {
				// Closed already, maybe by this very close whose answer was
				// lost; the shift as the store holds it now says which.
				const now = await loadShift().catch(() => null);
				const stillOpen = now?.open?.shiftId === shiftId;
				return {
					kind: 'refused',
					words: message(language, stillOpen ? 'ordersFailed' : 'shiftAlreadyClosed'),
				};
			}
			if (code === 'FORBIDDEN')
				return { kind: 'refused', words: message(language, 'shiftCloseForbidden') };
			if (code === 'NOT_A_CASH_POINT')
				return { kind: 'refused', words: message(language, 'shiftNotCashPoint') };
			if (code === 'VALIDATION_ERROR')
				return { kind: 'refused', words: message(language, 'shiftTooLarge') };
			return { kind: 'refused', words: message(language, 'ordersFailed') };
		} catch {
			if (approval) return { kind: 'approval', result: { ok: false, failure: 'unreachable' } };
			return { kind: 'refused', words: message(language, 'offline') };
		}
	}

	function closedWith(closed: ClosedShift) {
		stopWatch();
		approving = false;
		result = closed;
		typed = '';
		failure = '';
		send = closeShiftAttempt();
		step = 'result';
		void loadShift().catch(() => undefined);
	}

	const approval = new PasscodeEntry<ClosedShift>(closedWith, async (userId, passcode) => {
		const outcome = await close({ userId, passcode });
		if (outcome.kind === 'closed') return { ok: true, value: outcome.closed };
		if (outcome.kind === 'approval') return outcome.result;
		// Refused for a reason of the shift's, not the manager's: back to the
		// count, which says why.
		approving = false;
		failure = outcome.words;
		return { ok: false, failure: 'refused' };
	});

	async function confirmCount() {
		if (countedMinor === null || closing) return;
		failure = '';
		if (!isManager) {
			approval.unpick();
			approving = true;
			void approvers.load();
			return;
		}
		closing = true;
		const timer = setTimeout(() => (slow = true), 300);
		try {
			const outcome = await close();
			if (outcome.kind === 'closed') closedWith(outcome.closed);
			else if (outcome.kind === 'refused') failure = outcome.words;
		} finally {
			clearTimeout(timer);
			closing = false;
			slow = false;
		}
	}

	async function printAgain() {
		if (printing) return;
		printing = true;
		printFailure = '';
		const timer = setTimeout(() => (printSlow = true), 300);
		try {
			printFailure = (await printShiftAgain(language)) ?? '';
		} finally {
			clearTimeout(timer);
			printing = false;
			printSlow = false;
		}
	}

	function primary() {
		if (step === 'ask') {
			stopWatch();
			failure = '';
			step = 'count';
		} else if (step === 'count') {
			void confirmCount();
		} else if (step === 'result') {
			onClose();
		}
	}

	function stateWords(state: 'asking' | 'answered' | 'not_reached'): string {
		if (state === 'answered') return message(language, 'shiftReceived');
		if (state === 'not_reached') return message(language, 'shiftCannotReach');
		return message(language, 'shiftAsking');
	}

	const primaryLabel = $derived(
		step === 'result'
			? message(language, 'shiftBackToShift')
			: step === 'count'
				? message(language, 'confirm')
				: message(language, received && !failure ? 'shiftNext' : 'shiftCloseAnyway'),
	);
	const primaryTestId = $derived(
		step === 'result'
			? 'close-shift-back'
			: step === 'count'
				? 'close-shift-confirm'
				: 'close-shift-next',
	);
	const secondaryLabel = $derived(
		step === 'ask'
			? message(language, 'shiftAskAgain')
			: step === 'result'
				? message(language, printSlow ? 'shiftPrinting' : 'shiftPrintAgain')
				: undefined,
	);
</script>

<Sheet
	open={open && !approving}
	title={message(language, step === 'result' ? 'shiftClosedTitle' : 'shiftClose')}
	onClose={step === 'result' ? onClose : () => !closing && onClose()}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="close-shift-close"
	testId="panel-close-shift"
	locked={closing}
	{primaryLabel}
	primaryIcon={step === 'count' ? 'check' : undefined}
	{primaryTestId}
	primaryDisabled={step === 'start' ||
		(step === 'ask' && asking) ||
		(step === 'count' && (countedMinor === null || closing))}
	primaryState={step === 'count' && slow ? 'pending' : 'idle'}
	pendingLabel={message(language, 'shiftClosing')}
	onPrimary={primary}
	{secondaryLabel}
	secondaryTestId={step === 'ask' ? 'close-shift-ask-again' : 'close-shift-print-again'}
	secondaryDisabled={step === 'ask' ? asking : printing}
	onSecondary={() => void (step === 'ask' ? ask() : printAgain())}
>
	<div class="close-shift">
		{#if failure}
			<Band tone="flag" testId="close-shift-error">{failure}</Band>
		{/if}

		{#if step === 'start'}
			{#if askSlow}
				<div class="wait">
					<Loading
						label={message(language, 'shiftAsking')}
						caption={message(language, 'shiftAsking')}
						size="md"
						testId="close-shift-starting"
					/>
				</div>
			{/if}
		{:else if step === 'ask'}
			<p class="question" data-testid="close-shift-ask">{message(language, 'shiftAskTitle')}</p>
			<div class="tills" style="--cols: minmax(0, 1fr) auto">
				{#each asked as till (till.tillId)}
					<Row testId="close-shift-till-{till.tillId}">
						<span class="name">{till.name}</span>
						<span class="state" class:flag={till.state === 'not_reached'}
							>{stateWords(till.state)}</span
						>
					</Row>
				{/each}
			</div>
		{:else if step === 'count'}
			<!-- The question and the box beside the keypad, as on Payment. No
			     expected figure, float or sale total: the count is blind. -->
			<div class="count">
				<div class="ask">
					<p class="question" data-testid="close-shift-count-question">
						{message(language, 'shiftCountQuestion')}
					</p>
					<div dir="ltr">
						<Field
							testId="close-shift-amount"
							label={message(language, 'shiftCountAmount')}
							value={countedMinor === null ? '–' : formatMinor(countedMinor, minorDigits)}
							mono
						/>
					</div>
				</div>
				<div class="pad">
					<Keypad
						testId="close-shift-pad"
						label={message(language, 'shiftCountAmount')}
						deleteLabel={message(language, 'backspace')}
						disabled={closing}
						canType={typeTender(typed, '0') !== null}
						canDelete={typed.length > 0}
						onKey={(key) => {
							const next = typeTender(typed, key);
							if (next !== null) typed = next;
						}}
						onBackspace={() => (typed = typed.slice(0, -1))}
					/>
				</div>
			</div>
		{:else if result}
			<div class="result" data-testid="close-shift-result">
				<StatCard
					testId="close-shift-overshort"
					tone="ink"
					value={overShort(language, result.overShortMinor, minorDigits)}
				/>
				<div class="pair">
					<StatCard
						testId="close-shift-expected"
						label={message(language, 'shiftExpected')}
						value={formatMinor(result.expectedMinor, minorDigits)}
						mono
					/>
					<StatCard
						testId="close-shift-counted"
						label={message(language, 'shiftCounted')}
						value={formatMinor(result.countedMinor, minorDigits)}
						mono
					/>
				</div>
				<div class="figures" style="--cols: minmax(0, 1fr) auto">
					<Row testId="close-shift-cash">
						<span>{message(language, 'shiftCashSales')}</span>
						<span class="money" dir="ltr">{formatMinor(result.cashMinor, minorDigits)}</span>
					</Row>
					<Row testId="close-shift-card">
						<span>{message(language, 'shiftCardSales')}</span>
						<span class="money" dir="ltr">{formatMinor(result.cardMinor, minorDigits)}</span>
					</Row>
					<Row testId="close-shift-voids">
						<span>{message(language, 'shiftVoids')} ({result.voids})</span>
						<span class="money" dir="ltr">
							{formatMinor(result.voidCashMinor + result.voidCardMinor, minorDigits)}
						</span>
					</Row>
					{#if result.cashOutMinor !== 0 || result.cashInMinor !== 0}
						<Row testId="close-shift-cash-out">
							<span>{message(language, 'shiftCashOut')}</span>
							<span class="money" dir="ltr">{formatMinor(result.cashOutMinor, minorDigits)}</span>
						</Row>
						<Row testId="close-shift-cash-in">
							<span>{message(language, 'shiftCashIn')}</span>
							<span class="money" dir="ltr">{formatMinor(result.cashInMinor, minorDigits)}</span>
						</Row>
					{/if}
					<Row testId="close-shift-no-sale">
						<span>{message(language, 'shiftNoSaleOpens')}</span>
						<span class="money" dir="ltr">{result.noSaleOpens}</span>
					</Row>
					<Row testId="close-shift-due">
						<span>{message(language, 'shiftDue')} ({result.dueCount})</span>
						<span class="money" dir="ltr">{formatMinor(result.dueMinor, minorDigits)}</span>
					</Row>
				</div>
				<DueByDriver {language} {minorDigits} closed={result} testId="close-shift-split" />
				{#if result.unreached.length > 0}
					<Band tone="flag" testId="close-shift-unreached">
						{fill(
							message(language, 'shiftNotReached'),
							result.unreached.join(isArabic(language) ? '، ' : ', '),
						)}
					</Band>
				{/if}
				{#if printFailure}
					<Band tone="flag" testId="close-shift-print-error">{printFailure}</Band>
				{/if}
			</div>
		{/if}
	</div>
</Sheet>

<ManagerApproval
	open={open && approving}
	{language}
	title={fill(message(language, 'shiftApprovalTitle'), tillName)}
	people={approvers.cashiers}
	status={approvers.status}
	entry={approval}
	onClose={() => (approving = false)}
/>

<style>
	.close-shift {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.question {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 120px;
	}

	.state {
		color: var(--text-muted);
	}

	.state.flag {
		color: var(--signal-flag);
		font-weight: 600;
	}

	.count {
		display: flex;
		gap: var(--space-9);
	}

	.ask {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.pad {
		flex: none;
		align-self: flex-start;
	}

	.result {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.pair {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--space-5);
	}

	.money {
		font-family: var(--font-mono);
		font-weight: 600;
	}
</style>
